<template>
  <div class="">
    <slide-over-panel v-model:show="showEditModal">
      <template #title>
        <span class="font-semibold">{{ editingRow.Name }}</span> Snapshot Issues
      </template>
      <template #title-content>
        Any issues identified by the Alarm Snapshot feature are shown below. You can ignore these to
        stop getting alerts about points that don't need certain types of alarming, or click fix
        to start resolving the issue.
      </template>
      <template #content>
        <div>
          <ul class="grid grid-cols-1 gap-6 sm:grid-cols-1 lg:grid-cols-2">
            <snapshot-instance
              v-for="snapshot in snapshotsWithIssues(editingRow)"
              :key="snapshot.id"
              :snapshot="snapshot"
              @update:snapshot="updateSnapshot"
            >
            </snapshot-instance>
          </ul>
          <div v-if="snapshotsWithIssues(editingRow).length === 0" class="border-t border-gray-200">
            <div class="flex -mt-px">
              <div class="flex flex-1 w-0 border-gray-200">
                <a class="relative inline-flex py-3 items-center justify-center flex-1 w-0 border-2
                                                    border-blue-200 text-md leading-5 font-md font-sans rounded-md
                                                    text-blue-700 bg-white-600 hover:bg-blue-200 focus:outline-none
                                                    focus:ring-blue-200 focus:border-blue-400 active:bg-blue-300
                                                    rounded-bl-lg cursor-pointer focus:z-10 focus:no-underline"
                   :href="meterRoute(editingRow)"
                   target="_blank">
                  <span class="lnr icon-cake pr-2"></span>
                  Everything's Enabled! View the Point.
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>
    </slide-over-panel>

    <h2>Alarm Snapshot</h2>
    <div class="flex justify-between">
      <inline-tab-header
        :headers="filterHeaders"
        name="Filter By"
        :current="activeHeaderFilter"
        @update:current="updateCurrentFilter"
      ></inline-tab-header>
      <div class="m-2 ml-2 w-1/5">
        <select2-group :options='areaZones'
                       :close-on-select="true"
                       :allow-clear="true"
                       :multiple="false"
                       v-model='zone'
                       @input="this.loadData"
                       placeholder="Filter by Zone"
                       class="p-2 m-2 mt-4"
        ></select2-group>
      </div>
    </div>

    <ajax-table v-model:loading="ajaxTable.loading"
                v-model:search="ajaxTable.search"
                v-model:sort-by="ajaxTable.sortBy"
                v-model:sort-desc="ajaxTable.sortDesc"
                v-model:page="ajaxTable.page"
                v-model:items-per-page="ajaxTable.itemsPerPage"
                v-model:page-info="ajaxTable.pageInfo"
                :data="tableData"
                :headers="tableHeaders"
                :exportable="false"
                @loadData="loadData"
    >

      <!-- Snapshot Issue Column -->
      <template #col-1="tableProps">
        <div @click="openEditModal(tableProps.row)"
             class="pl-5 cursor-pointer"
             :class="{  'text-green-500' : snapshotOk(tableProps.row),
                                    'text-yellow-500' : snapshotErrorsIgnored(tableProps.row),
                                    'text-red-500' : snapshotErrors(tableProps.row)}">
          <span
            :class="{   'lnr icon-checkmark-circle' : snapshotOk(tableProps.row),
                                                    'lnr icon-warning': snapshotErrorsIgnored(tableProps.row),
                                                    'lnr icon-circle-minus' : snapshotErrors(tableProps.row)}"
            style="font-size: 2.0rem;"></span>
        </div>
      </template>

      <!-- Point Column -->
      <template #col-2="tableProps">
        <div class="div">
          <div class="flex flex-row flex-wrap">
            <div class="flex flex-wrap text-sm leading-5 font-medium text-blue-600 truncate">
              <a :href="company.buildCompanyRoute('meters/'+tableProps.row.ID)">
                {{ tableProps.row.Name }}
              </a>
              <span class="ml-1 font-normal text-gray-500">
                in {{ tableProps.row.zone.Name }}
              </span>
            </div>
          </div>
          <div class="flex flex-row flex-wrap">
            <div class="mt-2 flex">
              <div class="flex items-center text-sm leading-5 text-gray-500">
                <span class="lnr icon-home mr-2"></span>
                <span>
                  {{ tableProps.row.zone.area.Name }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Alarm Profile Column -->
      <template #col-3="tableProps">
        <div class="flex flex-col">
          <div class="flex flex-nowrap items-center">
            <span class="lnr icon-graph"></span>
            <a v-if="alarmProfileLink(tableProps.row) !== null" :href="alarmProfileLink(tableProps.row)">
              <span class="mx-2 px-1 text-sm leading-5 font-medium text-gray-600 truncate border-b border-dotted border-gray-400">{{ alarmProfileName(tableProps.row) }}</span>
            </a>
            <span v-else class="pl-2 text-sm leading-5 font-medium text-gray-600 truncate">{{ alarmProfileName(tableProps.row) }}</span>
          </div>
          <div class="flex flex-wrap w-full">
            <div class="">
              <div class="flex flex-nowrap items-center">
                <span :class="alarmProfileThreshold(tableProps.row) ? 'icon-cloud-check text-green-500' : 'icon-cloud-crossed text-red-500'"
                      class="lnr"></span>
                <span class="pl-2 text-sm leading-5 text-gray-500">Thresholds</span>
              </div>
              <div v-if="alarmProfileThreshold(tableProps.row) && alarmProfileThresholdResolve(tableProps.row)"
                   class="flex flex-nowrap items-center">
                <span class="lnr icon-sync text-green-500"></span>
                <span class="pl-2 text-sm leading-5 text-gray-500">Auto-Resolve</span>
              </div>
              <div class="flex flex-nowrap items-center">
                <span :class="alarmProfileOffline(tableProps.row) ? 'icon-cloud-check text-green-500' : 'icon-cloud-crossed text-red-500'"
                      class="lnr"></span>
                <span class="pl-2 text-sm leading-5 text-gray-500">Offline</span>
              </div>
              <div v-if="alarmProfileOffline(tableProps.row) && alarmProfileOfflineResolve(tableProps.row)"
                   class="flex flex-nowrap items-center">
                <span class="lnr icon-sync text-green-500"></span>
                <span class="pl-2 text-sm leading-5 text-gray-500">Auto-Resolve</span>
              </div>
            </div>

          </div>
        </div>
      </template>

      <!-- Alarm Thresholds Column -->
      <template #col-4="tableProps">
        <div class="mx-auto">
          <div class="flex flex-col items-center -ml-6 object-center">
            <div v-if="alarmProfileThresholdHigh(tableProps.row) || alarmProfileCautionThresholdHigh(tableProps.row)" class="pl-2 text-sm leading-5 text-gray-500">
              <span class="inline-flex items-center px-2.5 my-0.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-red-100 text-red-800">
                {{alarmProfileValueString(alarmProfileThresholdHigh(tableProps.row), alarmProfileCautionThresholdHigh(tableProps.row))}}
              </span>
            </div>
            <div v-if="alarmProfileThresholdHigh(tableProps.row) || alarmProfileCautionThresholdHigh(tableProps.row)" class="ml-0.5 pl-1 pb-1 lnr icon-exit-up2"></div>
            <div v-if="alarmProfileThresholdLow(tableProps.row) || alarmProfileCautionThresholdLow(tableProps.row)" class="ml-0.5 pl-1 lnr icon-exit-down2"></div>
            <div v-if="alarmProfileThresholdLow(tableProps.row) || alarmProfileCautionThresholdLow(tableProps.row)" class="pl-2 text-sm leading-5 text-gray-500">
              <span class="inline-flex items-center px-2.5  my-0.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-blue-100 text-blue-800">
                {{alarmProfileValueString(alarmProfileThresholdLow(tableProps.row), alarmProfileCautionThresholdLow(tableProps.row))}}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #col-5="tableProps">
        <div class="flex flex-col items-center ml-auto">
          <span @click="openEditModal(tableProps.row)"
                class="lnr icon-magnifier text-black-900"
                style="font-size: 2.0rem;">
          </span>
        </div>
      </template>
    </ajax-table>
  </div>
</template>

<script>
import axios from "axios"
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import AjaxTable from "@/components/elements/AjaxTable";
import SlideOverPanel from "@/components/elements/SlideOverPanel";
import Select2 from "@/components/elements/select2"
import ButtonBluePrimary from "@/components/elements/buttons/ButtonBluePrimary";
import ButtonGreenPrimary from "@/components/elements/buttons/ButtonGreenPrimary";
import _ from "lodash";
import UndecoratedLink from "@/components/EnterpriseDashboard/undecorated-link";
import SnapshotInstance from "@/components/SnapshotInstance";
import InlineTabHeader from "@/components/elements/InlineTabHeader";
import Select2Group from "@/components/elements/select2-group";

export default {
  name: "CalibrationIndex",
  mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin,],
  components: {
    UndecoratedLink, ButtonGreenPrimary, ButtonBluePrimary,
    SlideOverPanel, AjaxTable, Select2, SnapshotInstance, InlineTabHeader, Select2Group
  },
  props: {
    viewAllUrl: String,
    areaZones: Object,
  },
  data() {
    return {
      tableHeaders: [
        {
          name: 'Snapshot Issues',
          key: 'issue',
          sortBy: 'snapshot',
        },
        {
          name: 'Point',
          key: 'point',
          sortBy: 'point',
        },
        {
          name: 'Alarm Profile',
          key: 'meters',
          sortBy: '',
        },
        {
          name: 'Alarm Thresholds',
          key: 'enabled',
          sortBy: '',
        },
        {
          name: 'View Issues',
          key: 'enabled',
          sortBy: '',
        }
      ],
      tableData: [],
      allowedPoints: [],

      // Editing Modal
      showEditModal: false,
      editingRow: null,
      name: '',
      zone: null,
      showLifeTimesModal: false,
      requestNumber: 0,
      filterHeaders: [
        {
          text: 'All Points',
          event: 'all-points',
        },
        {
          text: 'Active Issues',
          event: 'active-issues',
        },
        {
          text: 'Active & Ignored Issues',
          event: 'all-issues',
        },
      ],
      activeHeaderFilter: null,
      showIssuesOnly: false,
      showIgnored: true,
    }
  },
  created() {
    // this.loadCompany()
  },
  mounted() {

  },
  methods: {
    loadData() {
      this.ajaxTable.loading = true
      this.ajaxTable.requestNumber++
      let params = JSON.parse(JSON.stringify(this.ajaxTable))
      params.requestNumber = this.ajaxTable.requestNumber
      params.showIssuesOnly = this.showIssuesOnly
      params.showIgnored = this.showIgnored
      if(this.zone){
        params.zone = this.zone
      }
      axios.get(this.company.buildCompanyApiInternalRoute('snapshot'), {
        params: params,
      }).then(response => {
        let data = response.data
        this.meters = []
        this.meters = data.data

        this.tableData = data.data

        this.ajaxUpdateTableData(this.tableData)
      }).catch(error => {
        console.log(error)
      }).then(() => {
        this.ajaxTable.loading = false
      })
    },
    updateSnapshot(event) {
      Object.keys(this.editingRow.snapshot).forEach(key => {
        if (this.editingRow.snapshot[key].id !== event.id) {
          return
        }
        this.editingRow.snapshot[key] = event
      })
    },
    openEditModal(row) {
      console.log('openEditModal triggered')
      this.editingRow = row
      this.showEditModal = true
    },
    snapshotErrors(row) {
      let result = false;
      if (row.snapshot !== undefined) {
        row.snapshot.forEach((snapshot, index) => {
          if (Boolean(snapshot.error) === true) {
            result = true;
          }
        })
      }
      return result;
    },
    snapshotErrorsIgnored(row) {
      // If there are errors, but they are ignored, then
      let result = false;
      if (row.snapshot !== undefined) {
        row.snapshot.forEach((snapshot, index) => {
          if (Boolean(snapshot.issue) === true && Boolean(snapshot.ignored) === true) {
            result = true;
          }
        })
      }
      return result;
    },
    snapshotOk(row) {
      return this.snapshotErrors(row) === false && this.snapshotErrorsIgnored(row) === false;
    },
    snapshotsWithIssues(row){
      return _.filter(row.snapshot, obj => {
        return Boolean(obj.issue) === true;
      })
    },
    meterRoute(row){
      return this.company.buildCompanyRoute('meters/' + row.ID);
    },
    alarmProfileLink(row) {
      if (row.alarm_profile === undefined) {
        return null;
      }
      return this.company.buildCompanyRoute('alarm-profiles/' + row.alarm_profile.id);
    },
    alarmProfileName(row) {
      if (row.alarm_profile === undefined) {
        return '';
      }
      return row.alarm_profile.name;
    },
    alarmProfileOffline(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      return row.alarm_profile.offline_alarms_enabled;
    },
    alarmProfileOfflineResolve(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      return row.alarm_profile.offline_resolve_enabled;
    },
    alarmProfileThresholdResolve(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      return row.alarm_profile.threshold_resolve_enabled;
    },
    alarmProfileThreshold(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      return row.alarm_profile.threshold_alarms_enabled;
    },
    alarmProfileThresholdHigh(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      if(_.isNil(row.alarm_profile.high_alarm_time) && row.alarm_profile.high_alarm_instant === false){
        return false;
      }
      return row.alarm_profile.high_alarm_value;
    },
    alarmProfileThresholdLow(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      if(_.isNil(row.alarm_profile.low_alarm_time) && row.alarm_profile.low_alarm_instant === false){
        return false;
      }
      return row.alarm_profile.low_alarm_value;
    },
    alarmProfileCautionThresholdHigh(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      if(_.isNil(row.alarm_profile.high_caution_time) && row.alarm_profile.high_caution_instant === false){
        return false;
      }
      return row.alarm_profile.high_caution_value;
    },
    alarmProfileCautionThresholdLow(row) {
      if (row.alarm_profile === undefined) {
        return false;
      }
      if(_.isNil(row.alarm_profile.low_caution_time) && row.alarm_profile.low_caution_instant === false){
        return false;
      }
      return row.alarm_profile.low_caution_value;
    },
    alarmProfileValueString(first, second){
      if(first === 0){
        first = first.toString();
      }
      if(second === 0){
        second = second.toString();
      }
      if(first && second){
        return first + ", " + second;
      }
      if(first){
        return first;
      }
      if(second){
        return second;
      }
      return '';
    },
    updateCurrentFilter(currentFilter) {
      this.page = 1
      this.showIssuesOnly = currentFilter === 'active-issues' ||
        currentFilter === 'all-issues'
      this.showIgnored = currentFilter === 'all-issues'
      this.activeHeaderFilter = currentFilter
      this.loadData()
    },
  },
  computed: {
    allowedPointsOptions() {
      let keyedNames = {}
      _.forEach(this.allowedPoints, function (val) {
        keyedNames[val.serial_number] = val.name;
      })
      return keyedNames;
    }
  },
  watch: {},
}
</script>

<style scoped>

</style>